//dependencies
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

//components
import Button from "../../../../../../components/Button";
import EmployeePicker, { EmployeeOption } from "../../../../../../components/EmployeePicker";
import ContentModal from "../../../../../../components/Modal/Templates/ContentModal";
import Typography from "../../../../../../components/Text";

//hooks
import {
	useGoalDetails,
	useGoalHistory,
	useMutateGoalDetails,
	useRbacObjectPermissions,
} from "../../../../../../api/hooks";
import useToast from "../../../../../../hooks/useToast";

import { Goal } from "../../../../../../api/services";

import { parseGoalPermissionsForUser } from "../../../utils";

//css
import css from "./ShareGoal.module.scss";

type ShareGoalModalProps = {
	handleClose: () => void;
	isOpen: boolean;
};

const ShareGoalModal = ({ isOpen, handleClose }: ShareGoalModalProps) => {
	const toast = useToast();

	const { goal_id: goalId } = useParams<{ goal_id: string }>();

	const { data: permissions } = useRbacObjectPermissions("goal", goalId || "");

	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: goalId || "" });
	const goal = data ? data.goal : ({} as Goal);
	const { refetch: refetchGoalProgress } = useGoalHistory({
		goalID: goalId || "",
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { userCanShareGoal } = parseGoalPermissionsForUser(permissions);

	const { refetch: refetchGoalActivity } = useGoalHistory({
		goalID: goalId || "",
		sortDirection: "desc",
		sortName: "_id",
	});
	const { mutate, isPending } = useMutateGoalDetails(goal?._id, {
		onSuccess: () => {
			refetchGoal();
			handleClose();
			refetchGoalActivity();
			refetchGoalProgress();
		},
		onError: () => {
			toast("Something went wrong, please try again", {
				type: "error",
				size: "small",
			});
		},
	});

	const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>(
		goal.sharers?.map(
			(sharer) =>
				({
					label: sharer.name,
					value: sharer._id,
				}) as EmployeeOption
		) || []
	);

	useEffect(() => {
		if (isOpen) {
			setSelectedEmployees(
				goal.sharers?.map(
					(sharer) =>
						({
							label: sharer.name,
							value: sharer._id,
						}) as EmployeeOption
				) || []
			);
		}
	}, [isOpen]);

	return (
		<ContentModal
			isOpen={isOpen}
			handleClose={handleClose}
			title={
				<Typography size="sub-heading" weight="semi-bold">
					Share Goal
				</Typography>
			}
			buttons={[
				<Button
					key="update-sharers"
					testid="update-sharers"
					color="green"
					isRequesting={isPending}
					disabled={!userCanShareGoal}
					onClick={() => {
						mutate([
							{
								field: "sharers",
								value: selectedEmployees.map((employee: any) => employee.value),
							},
						]);
					}}
				>
					Update
				</Button>,
			]}
		>
			<div className={css.content}>
				<EmployeePicker
					value={selectedEmployees}
					onChange={(value: EmployeeOption | EmployeeOption[]) =>
						setSelectedEmployees(
							value as Array<{ value: string; label: string; showRemove?: boolean | undefined }>
						)
					}
					isMultiSelect
					label="Share With"
				/>
			</div>
		</ContentModal>
	);
};

export default ShareGoalModal;
